// Definición del menú lateral del shell autenticado (Módulo Disciplinario).
// El RBAC de la UI se hace por ROL: cada ítem puede exigir una lista de `roles`;
// si el rol del usuario no está, no se muestra. Los ítems sin pantalla construida
// abren un Placeholder "en construcción".

export interface MenuItem {
  id: string;
  label: string;
  icon: string;
  roles?: string[];   // si se define, solo estos roles ven el ítem
  submenu?: MenuItem[];
}

// Roles con capacidad de administrar usuarios (directores + administrador).
export const ROLES_ADMIN_USUARIOS = [
  'DIRECTOR_INSTRUCCION',
  'DIRECTOR_JUZGAMIENTO',
  'DIRECTOR_SEGUNDA_INSTANCIA',
  'ADMINISTRADOR',
];

// Roles que gestionan el reparto (solo el Director de Instrucción + administrador).
export const ROLES_REPARTO = ['DIRECTOR_INSTRUCCION', 'ADMINISTRADOR'];

// Roles que gestionan el reparto de juzgamiento (Director de Juzgamiento + administrador).
export const ROLES_REPARTO_JUZGAMIENTO = ['DIRECTOR_JUZGAMIENTO', 'ADMINISTRADOR'];

// Roles de segunda instancia (ven la bandeja de segunda instancia).
export const ROLES_SEGUNDA_INSTANCIA_UI = [
  'SEGUNDA_INSTANCIA',
  'DIRECTOR_SEGUNDA_INSTANCIA',
  'ADMINISTRADOR',
];

// Roles que ven los reportes gerenciales (MÓDULO 7): administrador, directores y
// supervisión (solo lectura).
export const ROLES_REPORTES_UI = [
  'ADMINISTRADOR',
  'DIRECTOR_INSTRUCCION',
  'DIRECTOR_JUZGAMIENTO',
  'DIRECTOR_SEGUNDA_INSTANCIA',
  'SUPERVISOR',
  'APOYO_SUPERVISOR',
];

// Roles que gestionan notificaciones (MÓDULO 6): Secretaría, directores y admin.
export const ROLES_NOTIFICACIONES_UI = [
  'SECRETARIA',
  'DIRECTOR_INSTRUCCION',
  'DIRECTOR_JUZGAMIENTO',
  'DIRECTOR_SEGUNDA_INSTANCIA',
  'ADMINISTRADOR',
];

// Roles de PARTE del portal ciudadano (con login): disciplinado y defensor.
// Estos roles solo ven "Mis actuaciones" (ningún ítem interno del módulo).
export const ROLES_PARTE_UI = ['DISCIPLINADO', 'DEFENSOR'];

// Roles que pueden INICIAR el flujo (radicar la queja). Coincide con el backend
// de expedientes: incluye TECNOLOGO_APOYO; NO incluye Abogado especializado.
export const ROLES_RADICAR_UI = [
  'DIRECTOR_INSTRUCCION',
  'FUNCIONARIO_INSTRUCCION',
  'CONTRATISTA_INSTRUCCION',
  'TECNOLOGO_APOYO',
  'SECRETARIA',
  'ADMINISTRADOR',
];

// Nuevo flujo: solo el Técnico administrativo (+ Admin) CREA la pre-radicación (Etapa 1).
export const ROLES_CREADOR_PRERAD = ['TECNICO_ADMINISTRATIVO', 'ADMINISTRADOR'];
// El Libro radicador lo ven el creador (Etapa 1) y los fillers de la Etapa 2
// (abogado / tecnólogo de apoyo, que ahí ven sus casos asignados).
export const ROLES_LIBRO_RADICADOR = [
  'TECNICO_ADMINISTRATIVO',
  'ADMINISTRADOR',
  'CONTRATISTA_INSTRUCCION',
  'TECNOLOGO_APOYO',
];

// Roles que ven la bandeja de decisión inicial (doble visto bueno): abogado
// especialista, Director de Instrucción, técnico, tecnólogo y supervisión (lectura).
export const ROLES_DECISION_UI = [
  'ABOGADO_ESPECIALIZADO',
  'CONTRATISTA_INSTRUCCION',
  'DIRECTOR_INSTRUCCION',
  'TECNICO_ADMINISTRATIVO',
  'TECNOLOGO_APOYO',
  'SUPERVISOR',
  'APOYO_SUPERVISOR',
  'ADMINISTRADOR',
];

// Roles de solo lectura (supervisión): ven expedientes/reportes, no modifican.
export const ROLES_LECTURA_UI = ['SUPERVISOR', 'APOYO_SUPERVISOR'];

// Roles que ven expedientes (radicadores + abogado especializado + supervisión).
export const ROLES_INSTRUCCION_UI = [
  ...ROLES_RADICAR_UI,
  'ABOGADO_ESPECIALIZADO',
  'FUNCIONARIO_JUZGAMIENTO',
  'CONTRATISTA_JUZGAMIENTO',
  ...ROLES_LECTURA_UI,
];

export const MENU: MenuItem[] = [
  { id: 'inicio', label: 'Inicio', icon: 'home' },
  { id: 'mis-actuaciones', label: 'Mis actuaciones', icon: 'list', roles: ROLES_PARTE_UI },
  { id: 'usuarios', label: 'Usuarios', icon: 'users', roles: ROLES_ADMIN_USUARIOS },
  { id: 'mi-firma', label: 'Mi firma', icon: 'pen' },
  {
    id: 'instruccion', label: 'Instrucción', icon: 'folder',
    submenu: [
      { id: 'expedientes-no-iniciados', label: 'Expedientes no iniciados', icon: 'clock', roles: ROLES_CREADOR_PRERAD },
      { id: 'radicar-queja', label: 'Libro radicador', icon: 'book', roles: ROLES_LIBRO_RADICADOR },
      { id: 'decision-inicial', label: 'Decisión inicial', icon: 'gavel', roles: ROLES_DECISION_UI },
      { id: 'mis-expedientes', label: 'Mis expedientes', icon: 'folder', roles: ROLES_INSTRUCCION_UI },
      { id: 'instruccion-actuaciones', label: 'Actuaciones', icon: 'pencil' },
    ],
  },
  {
    id: 'juzgamiento', label: 'Juzgamiento', icon: 'gavel',
    submenu: [
      { id: 'juzgamiento-bandeja', label: 'Reparto Juzgamiento', icon: 'inbox', roles: ROLES_REPARTO_JUZGAMIENTO },
      { id: 'juzgamiento-audiencias', label: 'Audiencias', icon: 'scale' },
      { id: 'juzgamiento-fallos', label: 'Fallos', icon: 'gavel' },
    ],
  },
  {
    id: 'segunda', label: 'Segunda instancia', icon: 'scale',
    submenu: [
      { id: 'segunda-bandeja', label: 'Segunda instancia', icon: 'scale', roles: ROLES_SEGUNDA_INSTANCIA_UI },
    ],
  },
  {
    id: 'reparto', label: 'Reparto', icon: 'share',
    submenu: [
      { id: 'reparto-bandeja', label: 'Bandeja', icon: 'inbox', roles: ROLES_REPARTO },
      { id: 'reparto-carga', label: 'Carga laboral', icon: 'chart', roles: ROLES_REPARTO },
    ],
  },
  {
    id: 'notificaciones', label: 'Notificaciones', icon: 'bell',
    submenu: [
      { id: 'notificaciones-central', label: 'Central de notificaciones', icon: 'bell', roles: ROLES_NOTIFICACIONES_UI },
    ],
  },
  {
    id: 'terminos', label: 'Términos', icon: 'calendar',
    submenu: [
      { id: 'terminos-cronograma', label: 'Cronograma', icon: 'calendar' },
    ],
  },
  {
    id: 'reportes', label: 'Reportes', icon: 'chart',
    submenu: [
      { id: 'reportes', label: 'Reportes gerenciales', icon: 'chart', roles: ROLES_REPORTES_UI },
    ],
  },
];

/** ¿El usuario (por su rol) puede ver este ítem? */
export function puedeVer(item: MenuItem, rol: string): boolean {
  return !item.roles || item.roles.includes(rol);
}

/** Menú filtrado por rol: oculta ítems/submenús que el usuario no puede ver. */
export function menuVisible(rol: string): MenuItem[] {
  // Las partes del portal (disciplinado/defensor) solo ven "Mis actuaciones";
  // no se les muestra ningún ítem interno del módulo (ni "Inicio").
  if (ROLES_PARTE_UI.includes(rol)) {
    return MENU.filter((m) => m.id === 'mis-actuaciones');
  }
  return MENU.map((m) => {
    if (!m.submenu) return puedeVer(m, rol) ? m : null;
    const subs = m.submenu.filter((s) => puedeVer(s, rol)).map((s) => etiquetaPorRol(s, rol));
    return subs.length ? { ...m, submenu: subs } : null;
  }).filter((m): m is MenuItem => m !== null);
}

// Renombra ítems del menú según el rol. El Técnico administrativo NO decide en
// "Decisión inicial": su bandeja ahí solo recibe los casos DEVUELTOS para corregir la
// pre-radicación (Etapa 1), así que se le muestra como "Casos devueltos".
function etiquetaPorRol(item: MenuItem, rol: string): MenuItem {
  if (item.id === 'decision-inicial' && rol === 'TECNICO_ADMINISTRATIVO') {
    return { ...item, label: 'Casos devueltos' };
  }
  return item;
}
